import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { ASSETS } from "@/lib/constants";
import { useLang, LANGS } from "@/i18n";

const NAV_DEF = [
  { to: "/", key: "home" },
  {
    key: "about",
    children: [
      { to: "/about", key: "aboutMatha" },
      { to: "/idols", key: "idols" },
      { to: "/sacred-places", key: "places" },
    ],
  },
  { to: "/parampara", key: "parampara" },
  { to: "/events", key: "events" },
  { to: "/sanchara", key: "sanchara" },
  { to: "/gallery", key: "gallery" },
  // { to: "/daily-events", key: "daily" },
  { to: "/contact", key: "contact" },
  { to: "/sevas", key: "sevas" },
  
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t, lang, setLang } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#FDFBF7]/90 backdrop-blur-xl shadow-[0_2px_20px_rgba(60,40,20,0.06)]"
          : "bg-[#FDFBF7]/70 backdrop-blur-md"
      }`}
    >
      {/* Top ribbon */}
      <div className="border-b border-[#E5DAC7]/60">
        <div className="w-full px-3 sm:px-4 lg:px-6 flex items-center justify-between text-[11px] tracking-[0.22em] uppercase text-[#8a6a3a] py-1.5 font-medium">
          <span className="hidden md:inline">ಶ್ರೀಃ &nbsp;·&nbsp; {t.ribbon.tagline}</span>
          <span className="md:hidden">ಶ್ರೀಃ</span>
          <div className="flex items-center gap-4">
            <span className="hidden lg:inline">ॐ श्री माध्वाय नमः</span>
            <div data-testid="language-selector" className="flex items-center gap-0.5 bg-[#F4EBDB]/70 rounded-full p-0.5 normal-case tracking-normal">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1 rounded-full text-[11.5px] font-semibold transition-colors ${
                    lang === l.code
                      ? "bg-[#7B1818] text-[#FDFBF7]"
                      : "text-[#5C4D42] hover:bg-[#E6D5B8]/70"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-3 sm:px-4 lg:px-6">
        <div className="flex items-center justify-between gap-6 xl:gap-10 py-4 lg:py-5">
          {/* LEFT — Logo + brand text */}
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-4 group shrink-0">
            <img
              src={ASSETS.logo}
              alt="Sri Bhimanakatte Matha logo"
              className="h-14 w-14 lg:h-16 lg:w-16 xl:h-[72px] xl:w-[72px] rounded-full object-cover ring-1 ring-[#E5DAC7] shadow-sm"
            />
            <div className="leading-tight">
              <div className="font-display text-[#7B1818] text-xl sm:text-2xl lg:text-3xl xl:text-[32px] font-semibold tracking-tight leading-[1.05]">
                {t.matha.name}
              </div>
              <div className="text-[10.5px] lg:text-[11.5px] uppercase tracking-[0.22em] text-[#8a6a3a] mt-1 font-semibold">
                {t.matha.aka}
              </div>
              <div className="hidden lg:block text-[9.5px] uppercase tracking-[0.16em] text-[#a08560] mt-0.5">
                {t.matha.fullTitle}
              </div>
              <div className="hidden lg:block text-[9.5px] uppercase tracking-[0.16em] text-[#a08560] mt-0.5">
                {t.matha.subTitle}
              </div>
            </div>
          </Link>

          {/* RIGHT — Desktop nav */}
          <nav data-testid="desktop-nav" className="hidden xl:flex items-center gap-0.5 2xl:gap-1">
            {NAV_DEF.map((n) =>
              n.children ? (
                <div key={n.key} className="relative group">
                  <button
                    data-testid="nav-about-dropdown"
                    className={`flex items-center gap-1 px-3 2xl:px-4 py-2.5 rounded-full text-[15.5px] font-medium tracking-wide whitespace-nowrap transition-colors ${
                      n.children.some((c) => location.pathname === c.to)
                        ? "bg-[#7B1818] text-[#FDFBF7]"
                        : "text-[#2A2118] hover:bg-[#F4EBDB]"
                    }`}
                  >
                    {t.nav[n.key]}
                    <ChevronDown size={13} className="transition-transform duration-200 group-hover:rotate-180" />
                  </button>
                  <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200 z-50">
                    <div className="min-w-[210px] rounded-2xl bg-[#FDFBF7] shadow-[0_16px_40px_rgba(60,40,20,0.14)] ring-1 ring-[#E5DAC7] p-2">
                      {n.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          data-testid={`nav-${c.key}`}
                          className={({ isActive }) =>
                            `block px-4 py-2.5 rounded-xl text-[14px] font-medium transition-colors ${
                              isActive
                                ? "bg-[#7B1818] text-[#FDFBF7]"
                                : "text-[#2A2118] hover:bg-[#F4EBDB]"
                            }`
                          }
                        >
                          {t.nav[c.key]}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.key}`}
                className={({ isActive }) =>
                  `px-3 2xl:px-4 py-2.5 rounded-full text-[15.5px] font-medium tracking-wide whitespace-nowrap transition-colors ${
                    isActive
                      ? "bg-[#7B1818] text-[#FDFBF7]"
                      : "text-[#2A2118] hover:bg-[#F4EBDB]"
                  }`
                }
              >
                {t.nav[n.key]}
              </NavLink>
              )
            )}
          </nav>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(v => !v)}
            className="xl:hidden w-11 h-11 rounded-full bg-[#F4EBDB] text-[#7B1818] flex items-center justify-center"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="mobile-menu" className="xl:hidden border-t border-[#E5DAC7] bg-[#FDFBF7]">
          <nav className="px-4 py-4 flex flex-col gap-1">
            {NAV_DEF.map((n) =>
              n.children ? (
                <div key={n.key}>
                  <div className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-[0.2em] text-[#8a6a3a] font-semibold">
                    {t.nav[n.key]}
                  </div>
                  {n.children.map((c) => (
                    <NavLink
                      key={c.to}
                      to={c.to}
                      data-testid={`mobile-nav-${c.key}`}
                      className={({ isActive }) =>
                        `block pl-8 pr-4 py-3 rounded-xl text-sm font-medium ${
                          isActive
                            ? "bg-[#7B1818] text-[#FDFBF7]"
                            : "text-[#2A2118] hover:bg-[#F4EBDB]"
                        }`
                      }
                    >
                      {t.nav[c.key]}
                    </NavLink>
                  ))}
                </div>
              ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-${n.key}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-sm font-medium ${
                    isActive
                      ? "bg-[#7B1818] text-[#FDFBF7]"
                      : "text-[#2A2118] hover:bg-[#F4EBDB]"
                  }`
                }
              >
                {t.nav[n.key]}
              </NavLink>
              )
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
